import Splide from "@splidejs/splide";

const DESKTOP_MIN = 768;
const MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const ROOT_ATTRIBUTES = ["id", "role", "aria-label", "aria-labelledby"];

const snapshotRoot = element => ({
  className: element.className,
  attributes: ROOT_ATTRIBUTES.map(name => [name, element.getAttribute(name)]),
});

const restoreRoot = (element, snapshot) => {
  element.className = snapshot.className;

  snapshot.attributes.forEach(([name, value]) => {
    if (value === null) {
      element.removeAttribute(name);
    } else {
      element.setAttribute(name, value);
    }
  });
};

export const serviceSpeed = reduced => (reduced ? 0 : 600);

export const serviceOptions = reduced => ({
  type: "loop",
  perPage: 1,
  perMove: 1,
  arrows: true,
  autoplay: false,
  gap: 0,
  pagination: false,
  speed: serviceSpeed(reduced),
  arrowPath: "M 0 0",
  classes: {
    arrows: "splide__arrows p-top-service__arrows",
    arrow: "splide__arrow p-top-service__arrow",
    prev: "splide__arrow--prev p-top-service__prev",
    next: "splide__arrow--next p-top-service__next",
  },
});

export const serviceAction = ({ hasTarget, isDesktop, isMounted }) => {
  if (!hasTarget) {
    return "idle";
  }

  if (isDesktop) {
    return isMounted ? "destroy" : "idle";
  }

  return isMounted ? "idle" : "mount";
};

export const isDesktop = viewportWidth => viewportWidth >= DESKTOP_MIN;

export const syncMotion = (slider, reduced) => {
  if (slider) {
    slider.options = { speed: serviceSpeed(reduced) };
  }

  return slider;
};

export const mountService = (element, Slider = Splide, reduced = false) => {
  if (!element) {
    return null;
  }

  return new Slider(element, serviceOptions(reduced)).mount();
};

export const destroyService = slider => {
  if (slider) {
    slider.destroy(true);
  }

  return null;
};

export const syncService = (element, slider, viewportWidth, Slider = Splide, reduced = false) => {
  const action = serviceAction({
    hasTarget: Boolean(element),
    isDesktop: isDesktop(viewportWidth),
    isMounted: Boolean(slider),
  });

  if (action === "mount") {
    return mountService(element, Slider, reduced);
  }

  if (action === "destroy") {
    return destroyService(slider);
  }

  return slider;
};

export const initService = ({ root, viewport, motion, Slider = Splide } = {}) => {
  const page = root ?? document;
  const mediaQuery = viewport ?? window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`);
  const motionQuery = motion ?? window.matchMedia(MOTION_QUERY);
  const sliderElement = page.querySelector(".js-service-slider");

  if (!sliderElement) {
    return () => {};
  }

  const rootSnapshot = snapshotRoot(sliderElement);
  let slider = null;

  const syncSlider = () => {
    const viewportWidth = mediaQuery.matches ? DESKTOP_MIN : DESKTOP_MIN - 1;
    slider = syncService(sliderElement, slider, viewportWidth, Slider, motionQuery.matches);

    if (mediaQuery.matches && !slider) {
      restoreRoot(sliderElement, rootSnapshot);
    }
  };

  const updateMotion = () => {
    slider = syncMotion(slider, motionQuery.matches);
  };

  mediaQuery.addEventListener("change", syncSlider);
  motionQuery.addEventListener("change", updateMotion);
  syncSlider();

  return () => {
    mediaQuery.removeEventListener("change", syncSlider);
    motionQuery.removeEventListener("change", updateMotion);
    slider = destroyService(slider);
    restoreRoot(sliderElement, rootSnapshot);
  };
};

if (typeof document !== "undefined" && typeof window !== "undefined") {
  initService();
}
